/**
 * usePermissions Hook - V3 Permission-Based Authorization
 *
 * React hook that wraps permission utilities for convenient component usage.
 * Provides methods to check permissions and computed properties for common checks.
 *
 * @example
 * ```tsx
 * const { can, canAny, isAdmin } = usePermissions()
 *
 * if (can("tasks:write")) {
 *   // User can write tasks
 * }
 *
 * if (canAny(["tasks:write", "teams:manage"])) {
 *   // User can write tasks OR manage teams
 * }
 *
 * if (isAdmin) {
 *   // User is an admin
 * }
 * ```
 *
 * @see specs/auth-permission-refactor-v3/spec.md
 */

import {
  type Permission,
  hasAllPermissions,
  hasAnyPermission,
  hasPermission,
} from "@timebanken/services"
import { useCallback, useMemo } from "react"
import { useFirebase } from "./FirebaseContext"

// ============================================================================
// Types
// ============================================================================

/**
 * Return type for usePermissions hook
 */
export interface UsePermissionsReturn {
  /**
   * Check if user has a specific permission
   * @param permission - The permission to check
   * @returns true if user has the permission
   */
  can: (permission: Permission | string) => boolean

  /**
   * Check if user has any of the specified permissions (OR logic)
   * @param permissions - Array of permissions to check
   * @returns true if user has at least one of the permissions
   */
  canAny: (permissions: (Permission | string)[]) => boolean

  /**
   * Check if user has all of the specified permissions (AND logic)
   * @param permissions - Array of permissions to check
   * @returns true if user has all of the permissions
   */
  canAll: (permissions: (Permission | string)[]) => boolean

  /**
   * Computed property: true if user has users:write permission
   * Used to check if user can manage other users
   */
  isAdmin: boolean

  /**
   * True only for a trusted Firebase Auth platform claim.
   */
  isSuperAdmin: boolean

  /**
   * Raw permissions array from the user's role
   * Empty array if user has no role or no permissions
   */
  permissions: string[]
}

// ============================================================================
// Hook Implementation
// ============================================================================

/**
 * React hook for permission-based authorization.
 *
 * Uses the current user's role from FirebaseContext to check permissions.
 * Provides convenient methods and computed properties for common authorization patterns.
 *
 * @returns UsePermissionsReturn with permission checking methods and computed properties
 *
 * @example
 * ```tsx
 * function AdminButton() {
 *   const { can } = usePermissions()
 *
 *   if (!can("users:write")) {
 *     return null
 *   }
 *
 *   return <Button>Admin Action</Button>
 * }
 * ```
 */
export function usePermissions(): UsePermissionsReturn {
  const { userRole, isPlatformAdministrator } = useFirebase()
  const platform = isPlatformAdministrator === true

  // Extract permissions array with defensive handling
  const permissions = userRole?.permissions ?? []

  // Ensure permissions is always an array
  const safePermissions = useMemo(
    () => (Array.isArray(permissions) ? permissions.filter((permission) => permission !== "*") : []),
    [permissions],
  )
  const tenantRole = useMemo(
    () => userRole ? { ...userRole, permissions: safePermissions } : null,
    [userRole, safePermissions],
  )

  // Memoize callback functions to maintain stable references across renders.
  // Without this, any useEffect that depends on `can` would re-run every render.
  const can = useCallback(
    (permission: Permission | string): boolean => {
      return platform || hasPermission(tenantRole, permission)
    },
    [platform, tenantRole],
  )

  const canAny = useCallback(
    (perms: (Permission | string)[]): boolean => {
      return platform || hasAnyPermission(tenantRole, perms)
    },
    [platform, tenantRole],
  )

  const canAll = useCallback(
    (perms: (Permission | string)[]): boolean => {
      return platform || hasAllPermissions(tenantRole, perms)
    },
    [platform, tenantRole],
  )

  const isAdmin = useMemo(
    () => platform || hasPermission(tenantRole, "users:write"),
    [platform, tenantRole],
  )

  const isSuperAdmin = platform

  return {
    can,
    canAny,
    canAll,
    isAdmin,
    isSuperAdmin,
    permissions: safePermissions,
  }
}
